// src/components/backoffice/table-floor/AddEditTable.jsx
import React, { useEffect, useState } from "react";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { createRoom, createTable, getAllRoomsWithoutPagination, updateRoom, updateTable } from "@/api/table-floor/tableFloor";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllTables } from "@/redux/slice/table-slice/table-slice";
import { X } from "lucide-react";

const addNewFloorCardSchema = z.object({
  floor_id: z.number().min(1, "Floor is required"),
  room_id: z.number().min(1, "Room is required"),
  name: z.string().min(1, "Note is required"),
  seat: z.coerce.number().min(1, "Seat is required"),
});

const AddEditTable = ({ isOpen, onClose, formType, setFormType, itemsDialog }) => {
  const { floors, selectedRoom } = useSelector((state) => state.table.table);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState([])
  const { user } = useAuth();
  const dispatch = useDispatch();

  const form = useForm({
    resolver: zodResolver(addNewFloorCardSchema),
    mode: "onChange",
    defaultValues: {
      floor_id: "",
      room_id: "",
      name: "",
      seat: "",
    },
  });

  useEffect(() => {
    if (formType === "update" && itemsDialog) {
      form.reset({
        floor_id: itemsDialog.floor_id || "",
        room_id: itemsDialog.room_id || "",
        name: itemsDialog.name || "",
        seat: itemsDialog.seat || "",
        table_size: itemsDialog.table_size || "",
      });
    }
    if (formType === "create") {
      form.reset({
        floor_id: "",
        room_id: "",
        name: "",
        seat: "",
        table_size: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, itemsDialog]);

  const floorId = form.watch("floor_id");
  const [roomReset, setRoomReset] = useState(false)
  useEffect(() => {
    if(floorId){
      getAllRoomsWithoutPagination(user?.restaurant_id, floorId)
        .then((res) => {
          if (res.status === "success") {
            if(formType !== "update") {
              form.setValue("room_id", "");
            }
            if(formType === "update" && roomReset) {
              form.setValue("room_id", "");
            }
            setRooms(res.data.rooms);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [floorId]);

  const { reset } = form;

  const addFloorCardData = async (data) => {
    setLoading(true);

    const payload =
      formType === "update"
        ? {
            ...data,
            restaurant_id: user.restaurant_id,
            table_size: {
              ...itemsDialog.table_size,
              name: data.name,
            },
          }
        : {
            ...data,
            restaurant_id: user.restaurant_id,
            shape: "square",
            table_size: {
              name: data.name,
              id: `table${Date.now()}`,
              style: {
                left: 0,
                top: 0,
                backgroundColor: "#525355",
                transform: "translate(30px, 190px)",
                width: "180px",
                height: "180px",
                borderRadius: "5px 5px 5px 5px",
              },
              isMerged: false,
              mergedComponent: [],
              mergedName: "",
            },
          };

    try {
      let res;

      if (formType === "update") {
        res = await updateTable(itemsDialog.id, payload);
      } else {
        res = await createTable(payload);
      }

      if (res.status === "success") {
        if (formType === "update") {
          toast.success("Table updated successfully!");
        } else {
          toast.success("Table added successfully!");
        }
        onClose();
        reset();
        // IMPORTANT: refresh tables for selected room to reflect changes immediately
        if (data.room_id || selectedRoom?.id) {
          const roomId = data.room_id || selectedRoom?.id;
          dispatch(fetchAllTables(Number(roomId)));
        }
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Floor Card");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <Card className="bg-cblack4 text-cwhite w-[598px]">
        <CardHeader className="flex flex-row justify-between items-center py-4 px-6">
          <CardTitle className="text-white text-xl font-medium">{formType === "update" ? "Update Table" : formType === "view"? "View Details" : "Add New Table"}</CardTitle>
          <X onClick={()=>{onClose(); setRoomReset(false)}} className="cursor-pointer" size={16} />
        </CardHeader>
        <Separator />
        <CardContent>
          <div className="mt-4">
            <Form {...form}>
                {formType === 'view' ? 
                  <div className="flex flex-col gap-3">
                      <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Floor</label>
                          <input
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                              value={itemsDialog.floor.name}
                              readOnly
                          />
                      </div>
                      <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Room</label>
                          <input
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                              value={itemsDialog.room.name}
                              readOnly
                          />
                      </div>
                      <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Table Name / Number</label>
                          <input
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                              value={itemsDialog.name}
                              readOnly
                          />
                      </div>
                      <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Number of Seats</label>
                          <input
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                              value={itemsDialog.seat}
                              readOnly
                          />
                      </div>
                      <div className="mt-4 flex gap-5 w-full">
                        <Button isLoading={loading} className="w-full bg-cyellow text-base font-medium" onClick={()=>{setFormType('update')}}>
                          Edit
                        </Button>
                      </div>
                  </div> : 
                  <form onSubmit={form.handleSubmit(addFloorCardData)} className="flex flex-col gap-6">
                    <div onClick={()=>setRoomReset(true)}>
                      <FormInputField
                        name="floor_id"
                        style="bg-cblack3"
                        placeholder={"Select Floor"}
                        form={form}
                        type="select"
                        parseAs="string"
                        selectOption={floors?.filter(item => item.status !== "inactive")?.map((item) => {
                            return { label: item.name, value: item.id };
                        })}
                        label={"Floor"}
                        required={true}
                      />
                    </div>
                    <FormInputField
                      name="room_id"
                      style="bg-cblack3"
                      placeholder={"Select Room"}
                      form={form}
                      type="select"
                      parseAs="string"
                      selectOption={rooms?.filter(item => item.status !== "inactive")?.map((item) => {
                          return { label: item.name, value: item.id };
                      })}
                      label={"Room"}
                      required={true}
                    />
                    <FormInputField name="name" style="bg-cblack3" placeholder={"Write table name"} form={form} type="text" label={"Table Name / Number"} required={true} />
                    <FormInputField name="seat" style="bg-cblack3" placeholder={"Number of Seats"} form={form} type="number" label={"Number of Seats"} required={true} />

                    <div className="flex gap-5 w-full">
                      <Button isLoading={loading} className="w-full bg-[#36cc2a] text-base font-medium text-white">
                          {formType === "update" ? " Update" : " Confirm"}
                      </Button>
                    </div>
                  </form>
                }
            </Form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddEditTable;
